/* eslint-disable camelcase */
import {
  Autocomplete,
  Button,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { BoundaryRingSections } from "./EditBoundary";
import { DeepPartial, mergeDeep } from "portal/utils/objects";
import { getJobPath } from "portal/utils/routing";
import { Point } from "./Point";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { Section } from "portal/components/map/navigation/Section";
import { useCanEditFarmGeometry } from "portal/views/farms/permissions";
import { useFarmExplorer } from "portal/state/store";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Zone, Zone as ZoneType } from "protos/farm/farm";
import React, { FC, useMemo } from "react";

interface FieldProps {
  zone: ZoneType;
  updateZone: (newZone: DeepPartial<Zone>) => void;
}

export const Field: FC<FieldProps> = ({ zone, updateZone }: FieldProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const jobs = rtcJobsApi.endpoints.listJobs.useQuery({
    fieldIds: zone.id?.id ? [zone.id.id.toString()] : undefined,
  });

  const { pointsById, zonesById } = useFarmExplorer();
  const canEditGeometry = useCanEditFarmGeometry();

  const heading = zone.contents?.field?.plantingHeading;
  const a = heading?.abLine?.a?.id?.id
    ? pointsById?.[heading.abLine.a.id.id]
    : undefined;
  const b = heading?.abLine?.b?.id?.id
    ? pointsById?.[heading.abLine.b.id.id]
    : undefined;
  const plantingHeading = heading && (
    <Paper className="pl-4">
      <Point point={a} />
      <Point point={b} />
    </Paper>
  );
  const centerPivotIds = useMemo(
    () => zone.contents?.field?.centerPivotIds ?? [],
    [zone]
  );

  const { centerPivotZoneOptions } = useMemo(() => {
    return {
      centerPivotZoneOptions: zonesById
        ? Object.values(zonesById)
            .filter(
              (z) =>
                z.contents?.centerPivot &&
                !centerPivotIds.some((i) => i.id === z.id?.id)
            )
            .flatMap((z) => z.id ?? [])
        : [],
    };
  }, [zonesById, centerPivotIds]);

  return (
    <>
      <div className="flex flex-col h-full">
        <Section
          header={
            <Typography>
              {t("views.farms.detailsPanel.plantingHeading")}
            </Typography>
          }
        >
          {plantingHeading}
        </Section>
        <Section
          header={
            <Typography>
              {t("views.farms.zoneTypes.centerPivot_other")}
            </Typography>
          }
        >
          <div>
            <Autocomplete
              multiple
              disabled={!canEditGeometry}
              options={centerPivotZoneOptions}
              getOptionLabel={(option) =>
                zonesById?.[option.id]?.name ?? option.id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("views.farms.zoneTypes.centerPivot_other")}
                />
              )}
              value={centerPivotIds}
              onChange={(_e, newVal) => {
                const updatedZone = mergeDeep(zone, {
                  contents: {
                    field: {
                      centerPivotIds: newVal,
                    },
                  },
                  version: {
                    changed: true,
                  },
                });
                updateZone(updatedZone);
              }}
            />
          </div>
        </Section>
        <Section
          startCollapsed
          header={<Typography>{t("models.jobs.job_other")}</Typography>}
        >
          {jobs.data?.jobs.map((j) => (
            <div key={j.id} className="flex flex-col">
              <Button
                className="justify-start flex"
                onClick={() =>
                  navigate({
                    pathname: getJobPath(j.id.toString()),
                    search: `?returnTo=${location.pathname}`,
                  })
                }
                color="info"
                variant="text"
              >
                {j.name}
              </Button>
            </div>
          ))}
        </Section>
        <BoundaryRingSections zone={zone} />
      </div>
    </>
  );
};
